/**
 * Sync V2 Types
 * 
 * Event-based sync system types aligned with Flutter app implementation
 */

/**
 * Represents the type of sync event
 */
export enum SyncEventType {
  like = 'like',
  unlike = 'unlike',
  bookmark = 'bookmark',
  unbookmark = 'unbookmark',
  playProgress = 'playProgress',
  finish = 'finish',
  purchase = 'purchase',
  removeFromLibrary = 'removeFromLibrary',
}

/**
 * Represents the operation type
 */
export enum SyncOperation {
  create = 'create',
  update = 'update',
  delete = 'delete',
}

/**
 * Represents the status of a sync event
 */
export enum SyncStatus {
  idle = 'idle',
  pending = 'pending',
  syncing = 'syncing',
  completed = 'completed',
  failed = 'failed',
  conflicted = 'conflicted',
}

/**
 * Represents a single sync operation/event
 * 
 * This is the core data structure for the new sync system.
 * All user actions that need to be synced are captured as SyncEvents
 * and queued for synchronization.
 */
export interface SyncEvent {
  id: string
  userId: string
  type: SyncEventType
  entityType: string // 'liked', 'bookmarked', 'played_content', etc.
  entityId: string
  operation: SyncOperation
  data: Record<string, any>
  timestamp: string // ISO 8601 string
  version: number
  status: SyncStatus
  retryCount: number
  deviceId?: string // Device ID for device-aware conflict resolution
  lastAttempt?: string // ISO 8601 string
  errorMessage?: string
}

/**
 * Represents a change from the server
 */
export interface Change {
  entityType: string
  entityId: string
  operation: SyncOperation
  data: Record<string, any>
  updatedAt: string // ISO 8601 string
  version: number
  deviceId?: string // Device ID for device-aware conflict resolution
}

/**
 * Represents a conflict between local and remote data
 */
export interface Conflict {
  local: SyncEvent
  remote: Change
  entityType: string
  entityId: string
}

/**
 * Represents the result of a sync operation
 */
export interface SyncResult {
  success: boolean
  itemsSynced: number
  conflicts: SyncEvent[]
  serverTime?: string // ISO 8601 string
  errorMessage?: string
  hasChanges?: Record<string, boolean> // Indicates which types have changes
}

/**
 * Represents a conflict resolution decision
 */
export enum ConflictResolutionStrategy {
  useLocal = 'useLocal',
  useRemote = 'useRemote',
  merge = 'merge',
  userChoice = 'userChoice',
}

export interface ConflictResolution {
  strategy: ConflictResolutionStrategy
  resolvedChange?: Change
  mergedData?: Record<string, any>
  requiresUserChoice: boolean
}

/**
 * Incremental sync response from server
 */
export interface IncrementalSyncResponse {
  changes: Record<string, any[]> | Change[] // Can be array or object grouped by type
  has_changes?: Record<string, boolean> // Indicates which types have changes
  conflicts: Conflict[]
  server_time: string // ISO 8601 string
}

